import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { slideFromLeft, slideFromRight } from '@/lib/animations'

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: 'easeOut' } },
}

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
}

/* Parchment palette — matches MissionFounder / LandingFAQ */
const PARCHMENT = '#faf5e8'
const PARCHMENT_CARD = '#fffcf5'
const INK_DARK = '#1a0f00'
const INK_BODY = '#3d2e1e'
const INK_MUTED = '#5a4a38'
const GOLD = '#c9a030'
const GOLD_SOFT = 'rgba(201,160,48,0.12)'
const GOLD_BORDER = 'rgba(201,160,48,0.3)'

function SectionBadge({ children }) {
  return (
    <span
      className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest"
      style={{
        background: GOLD_SOFT,
        border: `1px solid ${GOLD_BORDER}`,
        color: '#a07820',
      }}
    >
      {children}
    </span>
  )
}

function VerseCallout({ text, ref: reference }) {
  return (
    <motion.blockquote
      initial={{ opacity: 0, x: -12 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6 }}
      className="my-10 pl-5 py-2 max-w-2xl"
      style={{ borderLeft: `3px solid ${GOLD}` }}
    >
      <p
        className="font-heading italic text-base md:text-lg leading-relaxed"
        style={{ color: INK_MUTED }}
      >
        &ldquo;{text}&rdquo;
      </p>
      <cite
        className="text-xs font-semibold not-italic tracking-wider uppercase mt-2 block"
        style={{ color: GOLD }}
      >
        {reference}
      </cite>
    </motion.blockquote>
  )
}

function PullQuote({ children }) {
  return (
    <motion.p
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55 }}
      className="font-heading font-extrabold text-3xl md:text-4xl leading-tight my-10 md:my-12 max-w-3xl"
      style={{ color: GOLD }}
    >
      &ldquo;{children}&rdquo;
    </motion.p>
  )
}

function SectionHeader({ eyebrow, title }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-80px' }}
      variants={stagger}
      className="mb-10 md:mb-14"
    >
      <motion.div variants={fadeUp}>
        <SectionBadge>{eyebrow}</SectionBadge>
      </motion.div>
      <motion.h2
        variants={fadeUp}
        className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold leading-[1.1] mt-5 max-w-3xl"
        style={{ color: INK_DARK }}
      >
        {title}
      </motion.h2>
    </motion.div>
  )
}

export default function About() {
  return (
    <div className="relative overflow-hidden" style={{ backgroundColor: PARCHMENT }}>
      {/* Golden-light texture overlay, matches MissionFounder */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'url(/golden-light.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.06,
          mixBlendMode: 'multiply',
        }}
      />

      <div className="relative z-10">
        {/* ═══════ HERO ═══════ */}
        <section className="pt-28 md:pt-36 pb-16 md:pb-20 px-4 md:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={stagger}
              className="grid md:grid-cols-[1fr_auto] gap-10 md:gap-14 items-center"
            >
              <div>
                <motion.div variants={fadeUp}>
                  <SectionBadge>My Story</SectionBadge>
                </motion.div>
                <motion.h1
                  variants={fadeUp}
                  className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight mt-6"
                  style={{ color: INK_DARK }}
                >
                  Saved at seven. Lost for a decade.{' '}
                  <span style={{ color: GOLD }}>Found again</span> in a youth-group room.
                </motion.h1>
                <motion.p
                  variants={fadeUp}
                  className="text-lg leading-relaxed mt-6 max-w-xl"
                  style={{ color: INK_BODY }}
                >
                  I'm Mason — founder of Illuminate Faith. This is the real version of how I got
                  here, and why I'm building this for you.
                </motion.p>
              </div>

              <motion.div variants={fadeUp} className="flex justify-center md:justify-end">
                <div className="relative">
                  <div
                    className="w-52 h-64 md:w-60 md:h-72 rounded-2xl overflow-hidden shadow-lg"
                    style={{ border: `3px solid ${GOLD_BORDER}` }}
                  >
                    <img
                      src="/founder.png"
                      alt="Mason — founder of Illuminate Faith"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div
                    className="absolute -bottom-3 -right-3 w-16 h-16 rounded-xl -z-10"
                    style={{ backgroundColor: 'rgba(201,160,48,0.15)' }}
                  />
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══════ SECTION 1 — THE DRIFT AND THE RETURN ═══════ */}
        <section className="px-4 md:px-8 py-16 md:py-20">
          <div className="max-w-3xl mx-auto">
            <SectionHeader eyebrow="Part One" title="The Drift and the Return" />

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
              variants={stagger}
              className="space-y-6 text-base md:text-lg leading-relaxed"
              style={{ color: INK_BODY }}
            >
              <motion.p variants={fadeUp}>
                I grew up in a strong Christian household. Both my parents love Jesus, and they
                raised me in that. When I was seven years old, I was sitting on the floor after
                church one day asking my mom who Jesus was. She walked me through it, and I gave my
                life to Him right there. I got baptized soon after. I remember being in awe of
                Christ and wanting to tell anyone I could about Him.
              </motion.p>

              <motion.p variants={fadeUp}>Then middle school happened.</motion.p>

              <motion.p variants={fadeUp}>
                I kept showing up to church every weekend — I just wasn't really there. I was
                checking a box because my parents made me go. I grew distant from the Lord and fell
                into real, named sin: lust, idolatry, pride, anger. I stayed there through most of
                high school.
              </motion.p>

              <PullQuote>Football was my god.</PullQuote>

              <motion.p variants={fadeUp}>
                Not hyperbole. My whole life plan was to play college football, and everything else
                ran second to that. Freshman year I walked into the linebacker room and the guy
                playing in front of me had already committed to a Division I school. I thought,{' '}
                <em>I have no chance.</em> I fell into a real depression. My idol had a deadline,
                and I had already missed it.
              </motion.p>

              <motion.p variants={fadeUp}>
                Sophomore year I started dating a girl — a relationship that pulled me further from
                God. Around the same time, my dad pulled me aside and asked if I wanted to move to a
                different town for a better football situation. I said yes immediately. That Sunday,
                our pastor preached about a briefcase — you don't know what's in it until you open
                it. I took that as my sign. We moved.
              </motion.p>

              <motion.p variants={fadeUp}>
                My mom started pushing me to try the youth group at the new church.{' '}
                <em>You need to go, you need to go, you need to go.</em> I had every excuse not to —
                football, school, the girl, whatever. About a year into the move, the girl broke up
                with me. I had nothing else on my schedule that Wednesday. So I went.
              </motion.p>

              <motion.p variants={fadeUp}>I liked it. I kept going.</motion.p>

              <motion.p variants={fadeUp}>
                Four weeks in, the youth group ran a student panel. One of my best friends was up
                there. Someone asked him,{' '}
                <em>What is something you learned about God this weekend?</em> He answered,{' '}
                <em>We don't share the gospel enough.</em>
              </motion.p>

              <motion.p variants={fadeUp} className="font-semibold" style={{ color: INK_DARK }}>
                It absolutely wrecked me.
              </motion.p>

              <motion.p variants={fadeUp}>
                I realized I wasn't living for the Lord. I hadn't been for years. I was living in my
                sin and calling it Christian because I showed up on Sundays. I went home that night
                and I wept. And I gave whatever the rest of my life was going to look like back to
                Him.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* ═══════ SECTION 2 — LIFE WITH CHRIST ═══════ */}
        <section className="px-4 md:px-8 py-16 md:py-20">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7 }}
              className="rounded-2xl overflow-hidden mb-12 md:mb-16 shadow-lg"
              style={{ border: `3px solid ${GOLD_BORDER}` }}
            >
              <img
                src="/SaturdayAfternoon.jpg"
                alt="Worship — hands lifted in a church service"
                className="w-full h-[260px] md:h-[440px] object-cover"
                loading="lazy"
              />
            </motion.div>

            <div className="max-w-3xl mx-auto">
              <SectionHeader eyebrow="Part Two" title="Life with Christ" />

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-60px' }}
                variants={stagger}
                className="space-y-6 text-base md:text-lg leading-relaxed"
                style={{ color: INK_BODY }}
              >
                <motion.p variants={fadeUp}>
                  Since that night, I've watched the Lord do things I don't have categories for.
                </motion.p>

                <motion.p variants={fadeUp}>
                  The one I come back to most: my freshman year of college at A&amp;M Galveston, I
                  started praying a simple prayer —{' '}
                  <em>
                    God, what do You want me to do with my life? How do You want me to serve You?
                  </em>{' '}
                  For five months, nothing. Silence. I kept praying it anyway.
                </motion.p>

                <motion.p variants={fadeUp}>
                  Then my cousin — who's a youth pastor — asked me to come help serve at a church
                  youth event. I said yes without overthinking it. On the last day there was a time
                  set aside for prayer, and I prayed the same prayer I'd been praying for five
                  months.
                </motion.p>

                <motion.p variants={fadeUp}>
                  Five minutes later, my cousin walked up to me and said,{' '}
                  <em>
                    Mason, you need to be a youth pastor. I feel like that's where God is calling
                    you.
                  </em>
                </motion.p>

                <motion.p variants={fadeUp}>
                  Five months of silence. Then Him putting the answer in the mouth of a person
                  standing five feet away from me.
                </motion.p>

                <motion.p variants={fadeUp}>
                  Today I'm interning at a church, leading youth kids. That prayer didn't get
                  answered quietly — it got answered <em>through the people He put next to me.</em>{' '}
                  I've also watched Him heal me from an injury the doctors couldn't explain, change
                  friends I thought were too far gone, and open doors I never asked for. But that
                  one prayer is the one I'll tell at my funeral.
                </motion.p>

                <VerseCallout
                  text="But he said to me, 'My grace is sufficient for you, for my power is made perfect in weakness.' Therefore I will boast all the more gladly about my weaknesses, so that Christ's power may rest on me."
                  ref="2 Corinthians 12:9 (NIV)"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ═══════ SECTION 3 — WHY ILLUMINATE ═══════ */}
        <section className="px-4 md:px-8 py-16 md:py-20">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
              className="grid md:grid-cols-2 gap-10 md:gap-16 items-center"
            >
              <motion.div
                variants={slideFromLeft}
                className="flex justify-center md:justify-start order-2 md:order-1"
              >
                <div className="relative">
                  <div
                    className="w-72 h-96 md:w-80 md:h-[28rem] rounded-2xl overflow-hidden shadow-lg"
                    style={{ border: `3px solid ${GOLD_BORDER}` }}
                  >
                    <img
                      src="/crazy2.jpeg"
                      alt="Mason leading a student game with a viking helmet"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div
                    className="absolute -bottom-3 -right-3 w-20 h-20 rounded-xl -z-10"
                    style={{ backgroundColor: 'rgba(201,160,48,0.15)' }}
                  />
                </div>
              </motion.div>

              <motion.div variants={slideFromRight} className="order-1 md:order-2">
                <SectionHeader eyebrow="Part Three" title="Why I'm Building Illuminate" />

                <div
                  className="space-y-5 text-base md:text-lg leading-relaxed"
                  style={{ color: INK_BODY }}
                >
                  <p>
                    If there are two gifts I feel God has given me, it's <strong>knowledge</strong>{' '}
                    and <strong>evangelism</strong>. I love digging into Scripture. I love defending
                    what I believe. I love sharing the gospel — with people who've never heard it,
                    or who grew up hearing it and walked away, the way I did.
                  </p>
                  <p>
                    But here's the problem I kept running into. When I had a real theological
                    question, I'd ask ChatGPT. The answer was generic. Surface-level. It would tell
                    me what a handful of religions believe and trail off — not what Scripture
                    actually says, not what the Church has taught for two thousand years.
                  </p>
                  <p>
                    I'd pick up commentaries. Commentaries are great — they're just long and dense,
                    and most believers don't have two hours to find an answer to one question in a
                    book.
                  </p>
                  <p>
                    So I built what I wanted to exist. Fast, theologically grounded answers, rooted
                    in the Bible, shaped by centuries of the Church, designed to actually help a
                    believer grow.
                  </p>
                  <p>
                    Illuminate is a one-stop hub for the average everyday Christian — Scripture
                    study, apologetics, sermon prep, Bible commentary. Tools for the people in the
                    pew, the small-group leaders, the students wrestling with what they believe, the
                    pastors writing Sunday's sermon.
                  </p>
                </div>

                <VerseCallout
                  text="Therefore go and make disciples of all nations, baptizing them in the name of the Father and of the Son and of the Holy Spirit."
                  ref="Matthew 28:19 (NIV)"
                />
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══════ SECTION 4 — WHERE I'M AT NOW ═══════ */}
        <section className="px-4 md:px-8 py-16 md:py-20">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7 }}
              className="rounded-2xl overflow-hidden mb-12 md:mb-16 shadow-lg"
              style={{ border: `3px solid ${GOLD_BORDER}` }}
            >
              <img
                src="/PineCove4.jpeg"
                alt="Mason riding a trick bike in front of a group of kids at a ministry event"
                className="w-full h-[240px] md:h-[420px] object-cover"
                loading="lazy"
              />
            </motion.div>

            <div className="max-w-3xl mx-auto">
              <SectionHeader eyebrow="Part Four" title="Where I'm At Now" />

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-60px' }}
                variants={stagger}
                className="space-y-6 text-base md:text-lg leading-relaxed"
                style={{ color: INK_BODY }}
              >
                <motion.p variants={fadeUp}>
                  I'm studying Industrial Engineering and Finance at Texas A&amp;M. I'm the chaplain
                  of a Christian fraternity on campus. I'm interning at a church, leading youth. I
                  serve in a fourth-grade ministry.
                </motion.p>
                <motion.p variants={fadeUp}>
                  I don't have it all figured out. I'm not claiming to. I still wrestle with the
                  same sins I named earlier. But I walk by faith, I seek His kingdom first, and I
                  keep building.
                </motion.p>
                <motion.p variants={fadeUp} className="font-semibold" style={{ color: INK_DARK }}>
                  Illuminate is the thing I'd want if I were still drifting. Maybe it's the thing
                  you'd want too.
                </motion.p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ═══════ GOOFY STRIP ═══════ */}
        <section className="px-4 md:px-8 py-12 md:py-16">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
              variants={stagger}
              className="grid grid-cols-2 gap-4 md:gap-6 mb-6"
            >
              {[
                { src: '/crazy.jpeg', alt: 'Mason getting his head shaved on stage at Pine Cove' },
                { src: '/crazy3.jpeg', alt: 'Mason in a full purple grape costume' },
              ].map(img => (
                <motion.div
                  key={img.src}
                  variants={fadeUp}
                  className="rounded-2xl overflow-hidden shadow-md aspect-[4/5]"
                  style={{ border: `3px solid ${GOLD_BORDER}` }}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </motion.div>
              ))}
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center font-heading italic text-base md:text-lg"
              style={{ color: INK_MUTED }}
            >
              I don't take myself too seriously. The Lord does all the lifting.
            </motion.p>
          </div>
        </section>

        {/* ═══════ CTA ═══════ */}
        <section className="px-4 md:px-8 pt-12 pb-28 md:pt-16 md:pb-36">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={fadeUp}
            className="max-w-3xl mx-auto text-center"
          >
            <h2
              className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight"
              style={{ color: INK_DARK }}
            >
              Walk with me.
            </h2>
            <p className="text-lg mt-5 max-w-xl mx-auto" style={{ color: INK_BODY }}>
              Illuminate is live and shipping new tools every few weeks. If the story resonates, try
              it free or jump in and start using the tools.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <Link
                to="/login?mode=signup"
                className="px-8 py-3.5 rounded-xl font-semibold transition-opacity hover:opacity-90"
                style={{ backgroundColor: GOLD, color: '#fffdf5' }}
              >
                Try it for free
              </Link>
              <Link
                to="/login"
                className="px-8 py-3.5 rounded-xl font-semibold transition-colors"
                style={{
                  border: `1px solid ${GOLD_BORDER}`,
                  color: GOLD,
                  backgroundColor: PARCHMENT_CARD,
                }}
              >
                Start Your Journey
              </Link>
            </div>
          </motion.div>
        </section>
      </div>
    </div>
  )
}
